import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import Contact from "./pages/Contact";
import About from "./pages/About";
import Services from "./pages/Services";
import Softwave from "./pages/Softwave";
import RedLightTherapy from "./pages/RedLightTherapy";
import SpinalDecompression from "./pages/SpinalDecompression";
import AppointmentScheduling from "./pages/AppointmentScheduling";
import WhatWeTreat from "./pages/WhatWeTreat";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import Specials from "./pages/Specials";
import ChiroThin from "./pages/ChiroThin";
import Testimonials from "./pages/Testimonials";
import Blog from "./pages/Blog";
import BlogPostDecompression from "./pages/BlogPostDecompression";
import BlogPostRedLight from "./pages/BlogPostRedLight";
import BlogPostInflammation from "./pages/BlogPostInflammation";
import BlogPostNeuropathy from "./pages/BlogPostNeuropathy";
import GoodFaithEstimate from "./pages/GoodFaithEstimate";
import Accessibility from "./pages/Accessibility";
import AntiDiscrimination from "./pages/AntiDiscrimination";
import BlogPostSoftwavePlantarFasciitis from "./pages/BlogPostSoftwavePlantarFasciitis";
import BlogPostSoftwaveKneePain from "./pages/BlogPostSoftwaveKneePain";
import BlogPostSoftwaveShoulderPain from "./pages/BlogPostSoftwaveShoulderPain";
import BlogPostSoftwaveTennisElbow from "./pages/BlogPostSoftwaveTennisElbow";
import BlogPostSoftwaveBackPain from "./pages/BlogPostSoftwaveBackPain";
import BlogPostDecompressionSciatica from "./pages/BlogPostDecompressionSciatica";
import BlogPostDecompressionDDD from "./pages/BlogPostDecompressionDDD";
import BlogPostDecompressionPinchedNerve from "./pages/BlogPostDecompressionPinchedNerve";
import BlogPostDecompressionNeckPain from "./pages/BlogPostDecompressionNeckPain";
import BlogPostDecompressionFailedSurgery from "./pages/BlogPostDecompressionFailedSurgery";
import BlogPostRedLightMuscleRecovery from "./pages/BlogPostRedLightMuscleRecovery";
import BlogPostRedLightBodyContouring from "./pages/BlogPostRedLightBodyContouring";
import BlogPostRedLightSkin from "./pages/BlogPostRedLightSkin";
import BlogPostRedLightJointPain from "./pages/BlogPostRedLightJointPain";
import BlogPostRedLightEnergy from "./pages/BlogPostRedLightEnergy";
import BlogPostChiroThinDoctor from "./pages/BlogPostChiroThinDoctor";
import BlogPostChiroThinFadDiets from "./pages/BlogPostChiroThinFadDiets";
import BlogPostChiroThinJointPain from "./pages/BlogPostChiroThinJointPain";
import BlogPostChiroThinDiet from "./pages/BlogPostChiroThinDiet";
import BlogPostChiroThinMetabolism from "./pages/BlogPostChiroThinMetabolism";
import HealthcareDisclaimer from "./pages/HealthcareDisclaimer";
import Hipaa from "./pages/Hipaa";
import AIDisclaimer from "./pages/AIDisclaimer";
import Financials from "./pages/Financials";
import ScrollToTop from "./components/ScrollToTop";

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/longview-softwave-pain-management-tissue-regeneration" element={<Softwave />} />
          <Route path="/longview-red-light-therapy" element={<RedLightTherapy />} />
          <Route path="/longview-spinal-decompression" element={<SpinalDecompression />} />
          <Route path="/longview-chirothin-weight-loss" element={<ChiroThin />} />
          <Route path="/what-we-treat" element={<WhatWeTreat />} />
          <Route path="/appointment-scheduling" element={<AppointmentScheduling />} />
          <Route path="/specials" element={<Specials />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/spinal-decompression" element={<BlogPostDecompression />} />
          <Route path="/blog/red-light-therapy" element={<BlogPostRedLight />} />
          <Route path="/blog/inflammation-joint-pain" element={<BlogPostInflammation />} />
          <Route path="/blog/neuropathy-softwave" element={<BlogPostNeuropathy />} />
          <Route path="/blog/softwave-plantar-fasciitis" element={<BlogPostSoftwavePlantarFasciitis />} />
          <Route path="/blog/softwave-knee-pain" element={<BlogPostSoftwaveKneePain />} />
          <Route path="/blog/softwave-shoulder-pain" element={<BlogPostSoftwaveShoulderPain />} />
          <Route path="/blog/softwave-tennis-elbow" element={<BlogPostSoftwaveTennisElbow />} />
          <Route path="/blog/softwave-back-pain" element={<BlogPostSoftwaveBackPain />} />
          <Route path="/blog/decompression-sciatica" element={<BlogPostDecompressionSciatica />} />
          <Route path="/blog/decompression-degenerative-disc-disease" element={<BlogPostDecompressionDDD />} />
          <Route path="/blog/decompression-pinched-nerve" element={<BlogPostDecompressionPinchedNerve />} />
          <Route path="/blog/decompression-neck-pain" element={<BlogPostDecompressionNeckPain />} />
          <Route path="/blog/decompression-failed-back-surgery" element={<BlogPostDecompressionFailedSurgery />} />
          <Route path="/blog/red-light-muscle-recovery" element={<BlogPostRedLightMuscleRecovery />} />
          <Route path="/blog/red-light-body-contouring" element={<BlogPostRedLightBodyContouring />} />
          <Route path="/blog/red-light-skin-rejuvenation" element={<BlogPostRedLightSkin />} />
          <Route path="/blog/red-light-joint-pain" element={<BlogPostRedLightJointPain />} />
          <Route path="/blog/red-light-cellular-energy" element={<BlogPostRedLightEnergy />} />
          <Route path="/blog/chirothin-doctor-supervised" element={<BlogPostChiroThinDoctor />} />
          <Route path="/blog/chirothin-fad-diets" element={<BlogPostChiroThinFadDiets />} />
          <Route path="/blog/chirothin-joint-pain" element={<BlogPostChiroThinJointPain />} />
          <Route path="/blog/chirothin-dietary-guidelines" element={<BlogPostChiroThinDiet />} />
          <Route path="/blog/chirothin-reset-metabolism" element={<BlogPostChiroThinMetabolism />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-service" element={<TermsOfService />} />
          <Route path="/goodfaithestimate" element={<GoodFaithEstimate />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/anti-discrimination" element={<AntiDiscrimination />} />
          <Route path="/healthcare-disclaimer" element={<HealthcareDisclaimer />} />
          <Route path="/hipaa" element={<Hipaa />} />
          <Route path="/financials" element={<Financials />} />
          <Route path="/ai-disclaimer" element={<AIDisclaimer />} />
          
          {/* Redirects for legacy routes */}
          <Route path="/longview-softwave-new-patient-special" element={<Specials />} />
          <Route path="/longview-spinal-compression-new-patient-special" element={<Specials />} />
          <Route path="/scheduling-softwave" element={<AppointmentScheduling />} />
          <Route path="/scheduling-spinal-decompression" element={<AppointmentScheduling />} />
          <Route path="/scheduling-red-light" element={<AppointmentScheduling />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
